/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react"
import { capitalize, get } from "lodash"
import { Table } from "@4cplatform/elements/Organisms"
import { ConfirmationModal } from "@4cplatform/elements/Molecules"

// Helpers
import { ConfigContext } from "@4cplatform/elements/Config"
import { Toggle } from "@4cplatform/elements/Forms"
import moment from "moment/moment"
import { P, SmallText } from "@4cplatform/elements/Typography"
import { JourneyContext } from "../../../../../../journey.context"
import { getName } from "../../../../../../../../Helpers"

const BupaSwitchQuestionsTable = () => {
  const { GLOBAL_TITLES } = useContext(ConfigContext)
  const { formik, data } = useContext(JourneyContext)

  const isLocked = get(data, "journey.locked", false)
  const applicants = get(data, "journey.applicants", [])

  useEffect(() => {
    const bupaQuestions = get(formik.values, "bupa_questions", null)

    if (bupaQuestions === null) {
      applicants.forEach(applicant => {
        const applicantId = get(applicant, "id")

        const fields = [
          "applicant_being_covered_cancer_last_5_years",
          "applicant_had_a_heart_or_circulatory_condition_last_5_years",
          "applicant_had_any_arthritic_or_back_and_neck_or_spinal_condition_or_joint_replacements_last_2_years",
          "applicants_have_any_planned_or_pending_investigations_treatment_or_surgery"
        ]

        fields.forEach(field => {
          const applicantFieldRef = `bupa_questions.applicant_${applicantId}.${field}`

          formik.setFieldValue(applicantFieldRef, get(applicant.answers, field, false))
        })
      })
    }
  }, [formik.values])

  const applicantsLoading = false
  const [getCancerConfirmationApplicantFieldRef, setGetCancerConfirmationApplicantFieldRef] =
    useState(null)
  const [isCancerConfirmationModalOpen, setIsCancerConfirmationModalOpen] = useState(false)

  return (
    <>
      <Table
        data={applicants}
        isLoading={applicantsLoading}
        columns={[
          [
            {
              label: "Name",
              data: "first_name",
              minWidth: "180px",
              render: row =>
                getName({
                  data: get(row, "data"),
                  hasTitle: true,
                  globalTitles: GLOBAL_TITLES,
                  hideOtherTitle: true
                })
            },
            {
              label: "Details",
              data: "date_of_birth",
              minWidth: "180px",
              render: row => {
                const dob = moment(get(row, "data.date_of_birth"), "YYYY-MM-DD HH:mm")
                const age = moment().diff(dob, "years")
                return (
                  <SmallText margin="0">
                    Age {age}
                    &nbsp;&nbsp;
                    {capitalize(get(row, "data.type", "-"))}
                  </SmallText>
                )
              }
            }
          ],
          {
            label:
              "In the last 5 years has this applicant been covered or had any form of cancer that's not already excluded on their existing scheme?",
            data: "bupa_questions.applicant_being_covered_cancer_last_5_years",
            minWidth: "80px",
            render: row => {
              const applicantId = get(row, "data.id", "")
              const applicantFieldRef = `bupa_questions.applicant_${applicantId}.applicant_being_covered_cancer_last_5_years`

              return (
                <div style={{ marginTop: "20px" }}>
                  <Toggle
                    name={applicantFieldRef}
                    options={[
                      { order: 1, label: "No", value: false },
                      { order: 2, label: "Yes", value: true }
                    ]}
                    errorPrefix="none"
                    isHorizontal
                    labelWidth="39.5rem"
                    margin="0 0 2rem"
                    isRequired
                    isDisabled={isLocked}
                    onChange={val => {
                      if (val && !isCancerConfirmationModalOpen) {
                        setGetCancerConfirmationApplicantFieldRef(applicantFieldRef)
                        setIsCancerConfirmationModalOpen(true)
                      }

                      formik.setFieldValue(applicantFieldRef, val)
                    }}
                    value={get(
                      formik.values,
                      applicantFieldRef,
                      get(row, "data.answers.applicant_being_covered_cancer_last_5_years", false)
                    )}
                  />
                  {isCancerConfirmationModalOpen && (
                    <ConfirmationModal
                      title="Cancer"
                      onCancel={() => {
                        setIsCancerConfirmationModalOpen(false)
                        formik.setFieldValue(getCancerConfirmationApplicantFieldRef, false)
                      }}
                      onConfirm={() => {
                        setIsCancerConfirmationModalOpen(false)
                      }}
                    >
                      <div>
                        <P>
                          Bupa are unable to provide a quote based an answering yes for cancer,
                          please refer to the Bupa underwriting guide.
                        </P>
                      </div>
                    </ConfirmationModal>
                  )}
                </div>
              )
            }
          },
          {
            label:
              "In the last 5 years have any of the applicants had a heart or circulatory condition that's not already excluded on their existing scheme (for example coronary artery disease, angina and heart attacks, stroke, abnormal heart rhythms, blood clots or aneurysms, heart valve disorders, uncontrolled high cholesterol/blood pressure etc)?",
            data: "bupa_questions.applicant_had_a_heart_or_circulatory_condition_last_5_years",
            minWidth: "80px",
            render: row => {
              const applicantId = get(row, "data.id", "")
              const applicantFieldRef = `bupa_questions.applicant_${applicantId}.applicant_had_a_heart_or_circulatory_condition_last_5_years`

              return (
                <div style={{ marginTop: "20px" }}>
                  <Toggle
                    name={applicantFieldRef}
                    options={[
                      { order: 1, label: "No", value: false },
                      { order: 2, label: "Yes", value: true }
                    ]}
                    errorPrefix="none"
                    isHorizontal
                    labelWidth="39.5rem"
                    margin="0 0 2rem"
                    isRequired
                    isDisabled={isLocked}
                    onChange={val => {
                      formik.setFieldValue(applicantFieldRef, val)
                    }}
                    value={get(
                      formik.values,
                      applicantFieldRef,
                      get(
                        row,
                        "data.answers.applicant_had_a_heart_or_circulatory_condition_last_5_years",
                        false
                      )
                    )}
                  />
                </div>
              )
            }
          },
          {
            label:
              "In the last two years has the person to be covered had any arthritic conditions, and / or back and neck conditions and / or spinal conditions (including sciatica), or joint replacements?",
            data: "bupa_questions.applicant_had_any_arthritic_or_back_and_neck_or_spinal_condition_or_joint_replacements_last_2_years",
            minWidth: "80px",
            render: row => {
              const applicantId = get(row, "data.id", "")
              const applicantFieldRef = `bupa_questions.applicant_${applicantId}.applicant_had_any_arthritic_or_back_and_neck_or_spinal_condition_or_joint_replacements_last_2_years`

              return (
                <div style={{ marginTop: "20px" }}>
                  <Toggle
                    name={applicantFieldRef}
                    options={[
                      { order: 1, label: "No", value: false },
                      { order: 2, label: "Yes", value: true }
                    ]}
                    errorPrefix="none"
                    isHorizontal
                    labelWidth="39.5rem"
                    margin="0 0 2rem"
                    isRequired
                    isDisabled={isLocked}
                    onChange={val => {
                      formik.setFieldValue(applicantFieldRef, val)
                    }}
                    value={get(
                      formik.values,
                      applicantFieldRef,
                      get(
                        row,
                        "data.answers.applicant_had_any_arthritic_or_back_and_neck_or_spinal_condition_or_joint_replacements_last_2_years",
                        false
                      )
                    )}
                  />
                </div>
              )
            }
          },
          {
            label:
              "Do any of the applicants have any planned or pending investigations, treatment or surgery for any condition or symptom that's not already excluded on their existing scheme (this applies whether the treatment is planned privately or under the NHS)?",
            data: "bupa_questions.applicants_have_any_planned_or_pending_investigations_treatment_or_surgery",
            minWidth: "80px",
            render: row => {
              const applicantId = get(row, "data.id", "")
              const applicantFieldRef = `bupa_questions.applicant_${applicantId}.applicants_have_any_planned_or_pending_investigations_treatment_or_surgery`

              return (
                <div style={{ marginTop: "20px" }}>
                  <Toggle
                    name={applicantFieldRef}
                    options={[
                      { order: 1, label: "No", value: false },
                      { order: 2, label: "Yes", value: true }
                    ]}
                    errorPrefix="none"
                    isHorizontal
                    labelWidth="39.5rem"
                    margin="0 0 2rem"
                    isRequired
                    isDisabled={isLocked}
                    onChange={val => {
                      formik.setFieldValue(applicantFieldRef, val)
                    }}
                    value={get(
                      formik.values,
                      applicantFieldRef,
                      get(
                        row,
                        "data.answers.applicants_have_any_planned_or_pending_investigations_treatment_or_surgery",
                        false
                      )
                    )}
                  />
                </div>
              )
            }
          }
        ]}
        name="applicants"
        hasActions={false}
        hasPerPage={false}
        margin="0 0 30px"
      />
    </>
  )
}

BupaSwitchQuestionsTable.defaultProps = {}

BupaSwitchQuestionsTable.propTypes = {}

export default BupaSwitchQuestionsTable
